import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, FileText, X, Check, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";


export default function UploadForm() {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => { setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
      setUploadStatus(null);
    }
  };
  const handleFileSelect = (e) => {
    if (e.target.files?.length) {
      setFiles(prev => [...prev, ...Array.from(e.target.files)]);
      setUploadStatus(null);
    }
  };
  const removeFile = (index) => { setFiles(prev => prev.filter((_, i) => i !== index)); };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadStatus(null);
    let successCount = 0;
    let duplicateCount = 0;
    let failCount = 0;

    const promises = files.map(async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await axios.post(`${API_BASE}/upload`, formData);
        if (response.data.status === 'exists') {
          duplicateCount++;
        } else {
          successCount++;
        }
      } catch (err) {
        failCount++;
      }
    });

    try {
      await Promise.all(promises);


      if (failCount === 0) {
        if (duplicateCount > 0) {
          setUploadStatus({ type: 'success', message: `Processed ${successCount} new, skipped ${duplicateCount} duplicate(s).` });
        } else {
          setUploadStatus({ type: 'success', message: `Successfully processed ${successCount} resume(s).` });
        }
        setFiles([]);
        setTimeout(() => setUploadStatus(null), 5000);
      } else {
        setUploadStatus({
          type: 'error',
          message: `${successCount} new, ${duplicateCount} skipped, ${failCount} failed.`
        });
      }
    } catch (e) {
      setUploadStatus({ type: 'error', message: "Critical upload error." });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="glass-card p-6 w-full dark:bg-slate-800 dark:border-slate-700">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={clsx(
          "cursor-pointer group relative flex flex-col items-center justify-center w-full h-40 rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden",
          isDragging
            ? "border-brand-500 bg-brand-50 dark:bg-brand-900/20"
            : "border-slate-200 hover:border-brand-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
        )}
      >
        <input type="file" ref={fileInputRef} className="hidden" multiple accept=".pdf,.doc,.docx" onChange={handleFileSelect} />
        <div className="z-10 flex flex-col items-center text-center space-y-2 p-4">
          <div className={clsx(
            "p-3 rounded-full transition-colors duration-300",
            isDragging ? "bg-brand-100 text-brand-600 dark:bg-brand-900 dark:text-brand-300" : "bg-slate-100 text-slate-400 group-hover:bg-brand-50 group-hover:text-brand-500 dark:bg-slate-700 dark:text-slate-400 dark:group-hover:bg-slate-700 dark:group-hover:text-brand-400"
          )}>
            <Upload className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
            <span className="text-brand-600 dark:text-brand-400 font-semibold">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500">PDF, DOCX up to 10MB</p>
        </div>
      </div>

      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-2 overflow-hidden"
          >
            <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-400 uppercase font-semibold tracking-wider px-1">
              <span>Selected Files ({files.length})</span>
              <button onClick={() => setFiles([])} className="text-red-500 hover:text-red-700 hover:underline">Clear all</button>
            </div>

            <ul className="max-h-48 overflow-y-auto pr-1 space-y-2 scrollbar-thin">
              {files.map((file, idx) => (
                <motion.li
                  key={`${file.name}-${idx}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100 text-sm group dark:bg-slate-700/50 dark:border-slate-700"
                >
                  <div className="flex items-center truncate max-w-[85%]">
                    <FileText className="w-4 h-4 text-brand-500 mr-2 flex-shrink-0" />
                    <span className="truncate font-medium text-slate-700 dark:text-slate-200">{file.name}</span>
                    <span className="ml-2 text-xs text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); removeFile(idx); }} className="text-slate-400 hover:text-red-500 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </motion.li>
              ))}
            </ul>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleUpload}
              disabled={uploading}
              className={clsx(
                "w-full mt-4 py-3 px-4 rounded-xl font-semibold text-sm shadow-lg shadow-brand-100 dark:shadow-none flex items-center justify-center transition-all",
                uploading ? "bg-brand-400 text-white cursor-wait" : "bg-brand-600 hover:bg-brand-700 text-white"
              )}
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : "Upload & Analyze"}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {uploadStatus && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={clsx(
              "mt-4 p-3 rounded-lg flex items-start text-sm",
              uploadStatus.type === 'success' ? "bg-green-50 text-green-700 border border-green-100 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900/30" : "bg-red-50 text-red-700 border border-red-100 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/30"
            )}
          >
            {uploadStatus.type === 'success' ? <Check className="w-5 h-5 mr-2 flex-shrink-0 text-green-500" /> : <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 text-red-500" />}
            <div>
              <p className="font-semibold">{uploadStatus.type === 'success' ? 'Success' : 'Error'}</p>
              <p className="opacity-90">{uploadStatus.message}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
